import { EntityType } from '../../../types.generated';
import { useGrantedPrivileges } from './GrantedPrivilegesContext';
import { useUserContext } from '../../context/useUserContext';
import {
    canViewChangeLog,
    isPlatformManageEntityPrivilege,
    canPerformEntityMenuAction,
    canPerformEntityAction,
    canPerformSchemaColAction,
    canPerformStructuredPropertiesUpdate,
    canPerformStructuredPropertiesRemove,
    canPerformCustomPropertiesUpdate,
    canPerformCustomPropertiesRemove,
    canPerformRelatedTermAction,
    canEditEntityName,
    canEditLineage,
    canCreateDatasetQuery,
    canUpdateDatasetQuery,
    canDeleteDatasetQuery,
    canDeleteAssertion,
    canCreateDataProduct,
    canUpdateDataProduct,
    canDeleteDataProduct,
    canExportDataProduct,
    canBulkImportDataProduct,
    canUpdateDataProductGovStatus,
    canUpdateDataProductOwners,
    canUpdateDataProductTags,
    canUpdateDataProductGlossaryTerms,
    canUpdateDataProductDomains,
    canManageGlossary,
    resolveOwnershipTypePrivileges,
    resolveIngestionPrivileges,
    resolveSecretPrivileges,
    EntityMenuAction,
    SidebarPrivilegeAction,
    SchemaColAction,
} from './rbacUtilsService';

/**
 * A single hook that exposes all RBAC permission checks with grantedPrivileges
 * and platformPrivileges already bound internally. Components no longer need to
 * call useGrantedPrivileges() and useUserContext() separately — just use this hook.
 *
 * @example
 * const { canViewChangeLog, canPerformEntityMenuAction } = useRbac();
 * const showChangeLog = canViewChangeLog(entityType);
 * const canDelete = canPerformEntityMenuAction(entityType, 'DELETE');
 */
export const useRbac = () => {
    const grantedPrivileges = useGrantedPrivileges();
    const { platformPrivileges } = useUserContext();

    return {
        /** Raw values — available if a consumer needs them for a case not covered below. */
        grantedPrivileges,
        platformPrivileges,

        // -------------------------
        // Change Log
        // -------------------------
        canViewChangeLog: (entityType: EntityType) =>
            canViewChangeLog(entityType, grantedPrivileges, platformPrivileges),

        // -------------------------
        // Entity Manage (platform manage bypass, e.g. manageDatasets)
        // Used to gate disabled-tab permission tooltips: viewing these advanced tabs (Validation,
        // Stats, View Definition) requires manage, so view-only users get the unauthorized tooltip.
        // -------------------------
        canManageEntity: (entityType: EntityType) => isPlatformManageEntityPrivilege(entityType, platformPrivileges),

        // -------------------------
        // Entity Menu Actions
        // -------------------------
        canPerformEntityMenuAction: (entityType: EntityType, action: EntityMenuAction) =>
            canPerformEntityMenuAction(entityType, action, grantedPrivileges, platformPrivileges),

        // -------------------------
        // Generic Sidebar Actions
        // -------------------------
        canPerformEntityAction: (entityType: EntityType, privilegeName: string, action?: string) =>
            canPerformEntityAction({
                action,
                entityType,
                privilegeName,
                grantedPrivileges,
                platformPrivileges,
            }),

        // -------------------------
        // Schema / Column Actions
        // -------------------------
        canPerformSchemaColAction: (entityType: EntityType, action: SchemaColAction) =>
            canPerformSchemaColAction(entityType, action, grantedPrivileges, platformPrivileges),

        // -------------------------
        // Structured Properties
        // -------------------------
        canPerformStructuredPropertiesUpdate: (entityType: EntityType) =>
            canPerformStructuredPropertiesUpdate(entityType, grantedPrivileges, platformPrivileges),

        canPerformStructuredPropertiesRemove: (entityType: EntityType) =>
            canPerformStructuredPropertiesRemove(entityType, grantedPrivileges, platformPrivileges),

        // -------------------------
        // Custom Properties
        // -------------------------
        canPerformCustomPropertiesUpdate: (entityType: EntityType) =>
            canPerformCustomPropertiesUpdate(entityType, grantedPrivileges, platformPrivileges),

        canPerformCustomPropertiesRemove: (entityType: EntityType) =>
            canPerformCustomPropertiesRemove(entityType, grantedPrivileges, platformPrivileges),

        // -------------------------
        // Entity Name
        // -------------------------
        canEditEntityName: (entityType: EntityType) =>
            canEditEntityName(entityType, grantedPrivileges, platformPrivileges),

        // -------------------------
        // Glossary Related Terms
        // -------------------------
        canPerformRelatedTermAction: (parentEntityType: EntityType, action: EntityMenuAction) =>
            canPerformRelatedTermAction(parentEntityType, action, grantedPrivileges, platformPrivileges),

        // -------------------------
        // Lineage
        // -------------------------
        canEditLineage: (entityType: EntityType) =>
            canEditLineage({ entityType, privilegeName: 'LINEAGE', grantedPrivileges, platformPrivileges }),

        // -------------------------
        // Dataset Queries
        // -------------------------
        canCreateDatasetQuery: () => canCreateDatasetQuery(grantedPrivileges, platformPrivileges),
        canUpdateDatasetQuery: () => canUpdateDatasetQuery(grantedPrivileges, platformPrivileges),
        canDeleteDatasetQuery: () => canDeleteDatasetQuery(grantedPrivileges, platformPrivileges),

        // -------------------------
        // Dataset Assertions
        // -------------------------
        canDeleteAssertion: () => canDeleteAssertion(grantedPrivileges, platformPrivileges),

        // -------------------------
        // Data Products
        // -------------------------
        canCreateDataProduct: () => canCreateDataProduct(grantedPrivileges, platformPrivileges),
        canUpdateDataProduct: () => canUpdateDataProduct(grantedPrivileges, platformPrivileges),
        canDeleteDataProduct: () => canDeleteDataProduct(grantedPrivileges, platformPrivileges),
        canExportDataProduct: () => canExportDataProduct(grantedPrivileges, platformPrivileges),
        canBulkImportDataProduct: () => canBulkImportDataProduct(grantedPrivileges, platformPrivileges),
        canUpdateDataProductGovStatus: () => canUpdateDataProductGovStatus(grantedPrivileges, platformPrivileges),
        canUpdateDataProductOwners: () => canUpdateDataProductOwners(grantedPrivileges, platformPrivileges),
        canUpdateDataProductTags: () => canUpdateDataProductTags(grantedPrivileges, platformPrivileges),
        canUpdateDataProductGlossaryTerms: () =>
            canUpdateDataProductGlossaryTerms(grantedPrivileges, platformPrivileges),
        canUpdateDataProductDomains: () => canUpdateDataProductDomains(grantedPrivileges, platformPrivileges),

        // -------------------------
        // Glossary
        // -------------------------
        canManageGlossary: () => canManageGlossary(platformPrivileges),

        // -------------------------
        // Ownership Types
        // -------------------------
        resolveOwnershipTypePrivileges: () => resolveOwnershipTypePrivileges(grantedPrivileges, platformPrivileges),

        // -------------------------
        // Ingestion & Secrets
        // -------------------------
        resolveIngestionPrivileges: () => resolveIngestionPrivileges(grantedPrivileges, platformPrivileges),
        resolveSecretPrivileges: () => resolveSecretPrivileges(grantedPrivileges, platformPrivileges),

        // -------------------------
        // Sidebar Actions (entity-agnostic helper)
        // -------------------------
        canPerformSidebarAction: (entityType: EntityType, privilegeName: SidebarPrivilegeAction) =>
            canPerformEntityAction({
                entityType,
                privilegeName,
                grantedPrivileges,
                platformPrivileges,
            }),
    };
};
